import useAuthStore from "@/store/useAuthStore";
import React, { useEffect, useState } from "react";
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  MenuItem,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Autocomplete } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import Pagination from "@/components/Pagination";
import {
  useUsers,
  useUsageLogsTenant,
  tenantFeature,
  useTenants,
} from "@/hooks/useTenantDashboardService";
import dayjs from "dayjs";
import type { GroupedUsageLogs, UsageLogsPayload, Tenant } from "@/types/user";
import type { UsageLog } from "@/types/usageLogs";
import { renderSinglePlusMore } from "@/components/renderSinglePlusMore";
import { formatDateUS } from "@/utils/dateUtils";

const DATE_DISPLAY_FORMAT = "MM/DD/YYYY";
const DATE_API_FORMAT = "YYYY-MM-DD";

const inputSx = {
  height: "58px",
  color: "white",
  backgroundColor: "#2C293A",
  borderRadius: "9999px",
  paddingRight: "30px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#555",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#B8F890",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#B8F890",
  },
  "& input": {
    color: "white",
    padding: "0 14px",
    height: "58px",
    boxSizing: "border-box",
  },
  "& .MuiSvgIcon-root": {
    color: "white",
  },
};

const UsageLogsTreeTable: React.FC = () => {
  const [groupBy, setGroupBy] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedFeatureIds, setSelectedFeatureIds] = useState<
    { id: string; name: string }[]
  >([]);
  const [selectedUserIds, setSelectedUserIds] = useState<
    { id: string; name: string }[]
  >([]);

  const { role, tenantid: localTenantId } = useAuthStore();
  const isSuperAdmin = role === "SUPER_ADMIN";
  const [selectedTenantId, setSelectedTenantId] = useState("");
  const { data: userData } = useUsers({ currentAuthRole: role });

  const effectiveTenantId = isSuperAdmin
    ? selectedTenantId
    : localTenantId || "";

  const { data: tenants } = useTenants();
  const { data: tenantFeatureData } = tenantFeature(effectiveTenantId);

  const allUsers = userData?.data?.items ?? [];
  const allFeatures = tenantFeatureData ?? [];
  const [page, setPage] = useState(1);
  const limit = 10;

  useEffect(() => {
    const today = dayjs();
    const tenDaysAgo = today.subtract(10, "day");
    setEndDate(today.format(DATE_DISPLAY_FORMAT));
    setStartDate(tenDaysAgo.format(DATE_DISPLAY_FORMAT));

    if (!isSuperAdmin && localTenantId) {
      setSelectedTenantId(localTenantId);
    }
  }, [isSuperAdmin, localTenantId]);

  useEffect(() => {
    if (isSuperAdmin && tenants?.data?.length && !selectedTenantId) {
      const firstTenantId = tenants.data[0].tenant.id;
      setSelectedTenantId(firstTenantId);
    }
  }, [isSuperAdmin, tenants, selectedTenantId]);

  const payload: UsageLogsPayload = {
    tenantId: effectiveTenantId,
    startDate: startDate
      ? dayjs(startDate, DATE_DISPLAY_FORMAT).format(DATE_API_FORMAT)
      : "",
    endDate: endDate
      ? dayjs(endDate, DATE_DISPLAY_FORMAT).format(DATE_API_FORMAT)
      : "",
    featureIds: selectedFeatureIds.map((u) => u.id),
    groupBy: groupBy as "feature" | "user",
    userId: selectedUserIds.map((u) => u.id),
  };

  const isPayloadReady = Boolean(effectiveTenantId && startDate && endDate);

  const { data: tenantUsageData, refetch } = useUsageLogsTenant(
    effectiveTenantId,
    payload,
    page,
    limit,
    isPayloadReady,
  );

  const totalPages = tenantUsageData?.total
    ? Math.ceil(tenantUsageData.total / limit)
    : 1;

  useEffect(() => {
    if (isPayloadReady) {
      refetch();
    }
  }, [isPayloadReady, refetch]);

  const grouped: GroupedUsageLogs = (() => {
    const usageLogs = tenantUsageData?.data;
    if (groupBy && usageLogs && !Array.isArray(usageLogs)) {
      return usageLogs as GroupedUsageLogs;
    }
    return {
      Default: Array.isArray(usageLogs) ? usageLogs : [],
    };
  })();

  return (
    <div className="p-4 rounded-lg overflow-x-auto text-white">
      <h2 className="text-2xl font-semibold mb-5">Usage Logs</h2>
      <Box mt={7}>
        {isSuperAdmin && (
          <TextField
            select
            label="Tenants"
            value={selectedTenantId}
            onChange={(e) => setSelectedTenantId(e.target.value)}
            variant="outlined"
            InputLabelProps={{ sx: { color: "white" } }}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  sx: {
                    backgroundColor: "#3A3A3A",
                    color: "white",
                  },
                },
              },
            }}
            sx={{
              minWidth: 200,
              maxWidth: 220,
              flex: "1 1 220px",
              "& .MuiOutlinedInput-root": {
                ...inputSx,
                "& .MuiOutlinedInput-notchedOutline": {
                  border: selectedTenantId
                    ? "2px solid #B8F890"
                    : "1px solid transparent",
                },
              },
              "& .MuiInputLabel-root": {
                color: "white",
                "&.Mui-focused": {
                  color: "#B8F890",
                },
              },
              "& .MuiSelect-icon": {
                color: "#B8F890",
              },
              "& .MuiOutlinedInput-input": {
                color: "white",
                display: "flex",
                alignItems: "center",
                paddingTop: "0 !important",
                paddingBottom: "0 !important",
              },
            }}
          >
            <MenuItem value="" disabled={!tenants?.data?.length}>
              {tenants?.data?.length ? (
                <em>Select Tenant</em>
              ) : (
                <em>Loading...</em>
              )}
            </MenuItem>
            {tenants?.data?.map((item: Tenant) => (
              <MenuItem key={item.tenant.id} value={item.tenant.id}>
                {item.tenant.name}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Box>

      <Box
        display="flex"
        gap={2}
        flexWrap="wrap"
        mb={3}
        mt={2}
        p={2}
        sx={{ backgroundColor: "#1D1B27", borderRadius: 1 }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Start Date"
            value={startDate ? dayjs(startDate, DATE_DISPLAY_FORMAT) : null}
            format={DATE_DISPLAY_FORMAT}
            onChange={(newValue) =>
              setStartDate(newValue?.format(DATE_DISPLAY_FORMAT) || "")
            }
            slots={{
              openPickerIcon: () => (
                <CalendarMonthIcon
                  sx={{ color: "white", width: "20px", height: "20px" }}
                />
              ),
            }}
            slotProps={{
              textField: {
                variant: "outlined",
                size: "small",
                InputLabelProps: {
                  shrink: true,
                  sx: { color: "white" },
                },
                InputProps: {
                  sx: inputSx,
                },
                sx: {
                  minWidth: 140,
                  flex: "1 1 130px",
                  "& label": {
                    color: "white",
                    "&.Mui-focused": {
                      color: "#B8F890",
                    },
                  },
                },
              },
            }}
          />

          <DatePicker
            label="End Date"
            value={endDate ? dayjs(endDate, DATE_DISPLAY_FORMAT) : null}
            format={DATE_DISPLAY_FORMAT}
            onChange={(newValue) =>
              setEndDate(newValue?.format(DATE_DISPLAY_FORMAT) || "")
            }
            slots={{
              openPickerIcon: () => (
                <CalendarMonthIcon
                  sx={{ color: "white", width: "20px", height: "20px" }}
                />
              ),
            }}
            slotProps={{
              textField: {
                variant: "outlined",
                size: "small",
                InputLabelProps: {
                  shrink: true,
                  sx: { color: "white" },
                },
                InputProps: {
                  sx: inputSx,
                },
                sx: {
                  minWidth: 140,
                  flex: "1 1 130px",
                  "& label": {
                    color: "white",
                    "&.Mui-focused": {
                      color: "#B8F890",
                    },
                  },
                },
              },
            }}
          />
        </LocalizationProvider>

        <Autocomplete
          multiple
          options={allFeatures}
          getOptionLabel={(opt) => opt.name}
          value={selectedFeatureIds}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_, newVals) =>
            setSelectedFeatureIds(
              newVals.map((v) => ({ id: String(v.id), name: v.name })),
            )
          }
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.name}
            </li>
          )}
          renderTags={renderSinglePlusMore}
          slotProps={{
            paper: {
              sx: {
                backgroundColor: "#3A3A3A",
                color: "white",
              },
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Features"
              variant="outlined"
              InputLabelProps={{ sx: { color: "white" } }}
              sx={{
                minWidth: 200,
                flex: "1 1 220px",
                "& .MuiOutlinedInput-root": {
                  ...inputSx,
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "1px solid transparent",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    border: "2px solid #B8F890",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    border: "2px solid #B8F890",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "white",
                  "&.Mui-focused": {
                    color: "#B8F890",
                  },
                },
                "& .MuiAutocomplete-popupIndicator": {
                  color: "#B8F890",
                },
                "& .MuiSvgIcon-root": {
                  color: "#B8F890",
                },
                "& .MuiAutocomplete-tag": {
                  backgroundColor: "#B8F890",
                  color: "black",
                  fontSize: "0.75rem",
                  padding: "0 8px",
                  borderRadius: "9999px",
                },
              }}
            />
          )}
        />

        <Autocomplete
          multiple
          options={allUsers}
          getOptionLabel={(opt) => opt.name}
          value={selectedUserIds}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_, newVals) =>
            setSelectedUserIds(
              newVals.map((v) => ({ id: String(v.id), name: v.name })),
            )
          }
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.name}
            </li>
          )}
          renderTags={renderSinglePlusMore}
          slotProps={{
            paper: {
              sx: {
                backgroundColor: "#3A3A3A",
                color: "white",
              },
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Users"
              variant="outlined"
              InputLabelProps={{ sx: { color: "white" } }}
              sx={{
                minWidth: 200,
                flex: "1 1 220px",
                "& .MuiOutlinedInput-root": {
                  ...inputSx,
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "1px solid transparent",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    border: "2px solid #B8F890",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    border: "2px solid #B8F890",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "white",
                  "&.Mui-focused": {
                    color: "#B8F890",
                  },
                },
                "& .MuiAutocomplete-popupIndicator": {
                  color: "#B8F890",
                },
                "& .MuiSvgIcon-root": {
                  color: "#B8F890",
                },
                "& .MuiAutocomplete-tag": {
                  backgroundColor: "#B8F890",
                  color: "black",
                  fontSize: "0.75rem",
                  padding: "0 8px",
                  borderRadius: "9999px",
                },
              }}
            />
          )}
        />

        <TextField
          select
          label="Group By"
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value)}
          variant="outlined"
          InputLabelProps={{ sx: { color: "white" } }}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                sx: {
                  backgroundColor: "#3A3A3A",
                  color: "white",
                },
              },
            },
          }}
          sx={{
            minWidth: 200,
            maxWidth: 220,
            flex: "1 1 220px",
            "& .MuiOutlinedInput-root": {
              ...inputSx,
              "& .MuiOutlinedInput-notchedOutline": {
                border: groupBy ? "2px solid #B8F890" : "1px solid transparent",
              },
            },
            "& .MuiInputLabel-root": {
              color: "white",
              "&.Mui-focused": {
                color: "#B8F890",
              },
            },
            "& .MuiSelect-icon": {
              color: "#B8F890",
            },
            "& .MuiOutlinedInput-input": {
              color: "white",
              display: "flex",
              alignItems: "center",
              paddingTop: "0 !important",
              paddingBottom: "0 !important",
            },
          }}
        >
          <MenuItem value="feature">Feature</MenuItem>
          <MenuItem value="user">User</MenuItem>
        </TextField>
      </Box>

      <Box sx={{ width: "100%", bgcolor: "#1C1A26", borderRadius: "8px" }}>
        {grouped && typeof grouped === "object" ? (
          Object.entries(grouped).map(
            ([groupKey, logs]: [string, UsageLog[]], idx) => (
              <Accordion
                key={idx}
                defaultExpanded
                sx={{ bgcolor: "#2A2736", color: "white" }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: "white" }} />}
                >
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    {groupBy
                      ? `${groupBy.charAt(0).toUpperCase() + groupBy.slice(1)}: ${groupKey}`
                      : "Usage Logs"}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Table
                    size="small"
                    sx={{ borderCollapse: "separate", borderSpacing: 0 }}
                  >
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#1E1E2F" }}>
                        {[
                          "Date",
                          "Feature",
                          "User",
                          "Prompt Tokens",
                          "Completion Tokens",
                          "Total Tokens",
                        ].map((header) => (
                          <TableCell
                            key={header}
                            sx={{
                              color: "white",
                              borderColor: "#333",
                              fontWeight: 600,
                              fontSize: "0.875rem",
                              textAlign:
                                header === "Feature" ||
                                  header === "User" ||
                                  header === "Date"
                                  ? "left"
                                  : "center",
                            }}
                          >
                            {header}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {Array.isArray(logs) && logs.length > 0 ? (
                        logs.map((log: UsageLog, i: number) => (
                          <TableRow
                            key={i}
                            sx={{
                              backgroundColor:
                                i % 2 === 0 ? "#2A2A3B" : "#242432",
                              "&:hover": { backgroundColor: "#33334d" },
                            }}
                          >
                            <TableCell
                              sx={{ color: "#E0E0E0", borderColor: "#444" }}
                            >
                              {formatDateUS(log?.created_at, true)}
                            </TableCell>
                            <TableCell
                              sx={{ color: "#E0E0E0", borderColor: "#444" }}
                            >
                              {log.Feature?.name || "—"}
                            </TableCell>
                            <TableCell
                              sx={{ color: "#E0E0E0", borderColor: "#444" }}
                            >
                              {log.User?.name || "—"}
                            </TableCell>
                            <TableCell
                              sx={{
                                color: "#E0E0E0",
                                borderColor: "#444",
                                textAlign: "center",
                              }}
                            >
                              {log.prompt_tokens ?? "—"}
                            </TableCell>
                            <TableCell
                              sx={{
                                color: "#E0E0E0",
                                borderColor: "#444",
                                textAlign: "center",
                              }}
                            >
                              {log.completion_tokens ?? "—"}
                            </TableCell>
                            <TableCell
                              sx={{
                                color: "#E0E0E0",
                                borderColor: "#444",
                                textAlign: "center",
                              }}
                            >
                              {log.total_tokens ?? "—"}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            align="center"
                            sx={{
                              color: "#AAA",
                              fontStyle: "italic",
                              py: 3,
                              borderColor: "#333",
                            }}
                          >
                            No logs available.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            ),
          )
        ) : (
          <Typography sx={{ color: "white", padding: 2 }}>
            No data found.
          </Typography>
        )}
      </Box>
      <Box display="flex" justifyContent="center">
        <Pagination
          totalPages={totalPages}
          currentPage={page}
          setCurrentPage={setPage}
        />
      </Box>
    </div>
  );
};

export default UsageLogsTreeTable;
